import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styles from '../styles/TermsOfService.module.css'
import FooterSection from './FooterSection'
import ScrollToTop from '../../../components/ScrollToTop/ScrollToTop'
import footerLogo from '../../../assets/image/logo/thickWalletOnly.png'

const TermsOfService = () => {
    const history = useHistory();
    const lastUpdated = "December 22, 2025";

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBackClick = () => {
        history.push('/');
    };

    const handleBackKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBackClick();
        }
    };

    const handlePrivacyClick = () => {
        history.push('/privacy');
    };

    const handlePrivacyKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePrivacyClick();
        }
    };

    return (
        <div className={styles.terms_page}>
            <main className={styles.terms_main}>
                <div className="container">
                    <button 
                        className={styles.back_button}
                        onClick={handleBackClick}
                        onKeyDown={handleBackKeyDown}
                        tabIndex={0}
                        aria-label="Back to Home"
                    >
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path 
                                d="M19 12H5M5 12L12 19M5 12L12 5" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span>Back to Home</span>
                    </button>
                    <header className={styles.terms_header}>
                        <img 
                            src={footerLogo} 
                            alt="Thick Wallet Logo" 
                            className={styles.header_logo}
                        />
                        <h1 className={styles.terms_title}>Terms of Service</h1>
                        <p className={styles.terms_subtitle}>
                            Last Updated: {lastUpdated}
                        </p>
                    </header>

                    <section className={styles.terms_content}>
                        <div className={styles.terms_section}>
                            <h2>1. Acceptance of Terms</h2>
                            <p>
                                By accessing or using <strong>Thick Wallet</strong> ("Service"), you agree to be bound by these 
                                Terms of Service ("Terms"). If you do not agree to these Terms, please do not 
                                use our Service. These Terms constitute a legally binding agreement between you 
                                and Thick Wallet regarding your use of our platform.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>2. Nature of Service: Educational & Informational Only</h2>
                            <p>
                                Thick Wallet provides automated data analysis and technical insights into the 
                                Indian equity market (NSE/BSE). Our Service includes features such as market 
                                trends analysis, dip detection, watchlist management, and investment insights.
                            </p>
                            <div className={styles.warning_box}>
                                <p>
                                    <strong>Not a Recommendation:</strong> All insights provided (Short-term or Long-term) 
                                    are based on mathematical algorithms and historical data. They are <strong>not</strong> personal 
                                    recommendations to buy, sell, or hold any security.
                                </p>
                            </div>
                            <div className={styles.warning_box}>
                                <p>
                                    <strong>No SEBI Registration:</strong> Thick Wallet and its owners are <strong>not</strong> SEBI-registered 
                                    Investment Advisors (RIA) or Research Analysts (RA). We operate as a data analysis tool 
                                    providing purely data-driven insights with no personalized human input.
                                </p>
                            </div>
                            <p>
                                <strong>User Responsibility:</strong> You are solely responsible for your own investment decisions. 
                                We strongly recommend consulting a certified financial professional before trading.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>3. High-Risk Warning (Market Risk)</h2>
                            <div className={styles.risk_warning}>
                                <p>
                                    <strong>⚠️ Investment in the securities market is subject to market risks.</strong>
                                </p>
                                <ul>
                                    <li>Past performance is not an indicator of future results.</li>
                                    <li>Stock prices can fluctuate significantly in a short period.</li>
                                    <li>You may lose some or all of your invested capital.</li>
                                    <li>Read all scheme-related documents carefully before investing.</li>
                                </ul>
                            </div>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>4. User Accounts & Social Login</h2>
                            <p>To access certain features of our Service, you must create an account. You agree to:</p>
                            <ul>
                                <li>Provide accurate, current, and complete information during registration</li>
                                <li>Maintain and promptly update your account information</li>
                                <li>Maintain the security and confidentiality of your login credentials</li>
                                <li>Accept responsibility for all activities that occur under your account</li>
                                <li>Notify us immediately of any unauthorized use of your account</li>
                            </ul>
                            <p>
                                <strong>Social Login:</strong> You may log in using Google or Facebook SSO. You are 
                                responsible for maintaining the security of your social media accounts. By using 
                                these services, you authorize us to access certain information from your third-party 
                                account as permitted by those services.
                            </p>
                            <p>
                                We reserve the right to suspend accounts that provide false data or engage in 
                                "scraping" our stock insights.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>5. Privacy Policy</h2>
                            <p>
                                Your privacy is important to us. Our{' '}
                                <span 
                                    className={styles.link_text}
                                    onClick={handlePrivacyClick}
                                    onKeyDown={handlePrivacyKeyDown}
                                    tabIndex={0}
                                    role="link"
                                    aria-label="View Privacy Policy"
                                >
                                    Privacy Policy
                                </span>
                                {' '}explains how we collect, use, 
                                disclose, and protect your personal information in compliance with the 
                                Information Technology Act, 2000 and the Digital Personal Data Protection (DPDP) Act, 2023.
                            </p>
                            <p>
                                When you use third-party login services (Facebook, Google, etc.), we may receive 
                                information from those services in accordance with their privacy policies and your 
                                privacy settings on those platforms.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>6. Acceptable Use</h2>
                            <p>You agree not to use the Service to:</p>
                            <ul>
                                <li>Violate any applicable local, state, national, or international law including Indian laws</li>
                                <li>Infringe upon the rights of others, including intellectual property rights</li>
                                <li>Transmit any harmful, threatening, abusive, or otherwise objectionable content</li>
                                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                                <li>Use automated systems, robots, or data mining/scraping tools to access the Service</li>
                                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                                <li>Redistribute, sell, or commercially exploit our stock insights to third parties</li>
                            </ul>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>7. Intellectual Property</h2>
                            <p>
                                The proprietary algorithms, custom stock screeners, "Buying Insights", and all 
                                content, features, and functionality of the Service are the exclusive property of 
                                Thick Wallet or its licensors and are protected by copyright, trademark, and other 
                                intellectual property laws.
                            </p>
                            <p>
                                You are granted a limited, non-exclusive, non-transferable license to access and use 
                                the Service for your personal, non-commercial purposes. You may not reproduce, distribute, 
                                modify, create derivative works of, publicly display, or otherwise exploit any content 
                                from the Service without our express written permission.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>8. SEBI Regulatory Notice</h2>
                            <div className={styles.regulatory_notice}>
                                <p>
                                    Thick Wallet provides stock market insights and data analysis for <strong>educational 
                                    and informational purposes only</strong>. We are not a SEBI-registered Investment 
                                    Advisor (RIA) or Research Analyst (RA).
                                </p>
                                <p>
                                    Users are advised to consult with a certified financial advisor before making 
                                    any actual investment decisions in the Indian markets. Nothing contained on our 
                                    Service constitutes a solicitation, recommendation, endorsement, or offer by 
                                    Thick Wallet to buy or sell any securities or other financial instruments.
                                </p>
                            </div>
                            <div className={styles.compliance_table}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Type of Service</th>
                                            <th>Classification</th>
                                            <th>SEBI Requirement</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>General reports (One-to-Many)</td>
                                            <td>Research Analyst</td>
                                            <td>Registration if fee charged</td>
                                        </tr>
                                        <tr>
                                            <td>Personalized advice (One-to-One)</td>
                                            <td>Investment Advisor</td>
                                            <td>Strict registration required</td>
                                        </tr>
                                        <tr>
                                            <td>Data-driven insights (No human input)</td>
                                            <td>Data Tool ✓</td>
                                            <td>Disclaimer required (Our model)</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>9. Disclaimer of Warranties</h2>
                            <p>
                                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                                WHETHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL 
                                WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
                                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                            </p>
                            <p>
                                We do not warrant that the Service will be uninterrupted, timely, secure, or error-free. 
                                We make no warranties regarding the accuracy, reliability, or completeness of any market 
                                data, stock information, or analysis provided through the Service.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>10. Limitation of Liability</h2>
                            <p>
                                TO THE MAXIMUM EXTENT PERMITTED BY INDIAN LAW, THICK WALLET AND ITS OFFICERS, DIRECTORS, 
                                EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
                            </p>
                            <ul>
                                <li>Financial losses or lost profits from investment decisions</li>
                                <li>Loss of data, use, goodwill, or other intangible losses</li>
                                <li>Any investment decisions made based on information from the Service</li>
                                <li>Unauthorized access to your account</li>
                                <li>Any errors or omissions in market data or stock information</li>
                            </ul>
                            <p>
                                Use of the app is "As Is" and "As Available."
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>11. Indemnification</h2>
                            <p>
                                You agree to indemnify, defend, and hold harmless Thick Wallet and its officers, 
                                directors, employees, agents, and affiliates from and against any claims, liabilities, 
                                damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out 
                                of or related to your use of the Service, your violation of these Terms, or your 
                                violation of any rights of another party.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>12. Third-Party Services</h2>
                            <p>
                                Our Service may contain links to third-party websites or services that are not owned or 
                                controlled by Thick Wallet. We have no control over, and assume no responsibility for, 
                                the content, privacy policies, or practices of any third-party websites or services.
                            </p>
                            <p>
                                When you use third-party authentication services (such as Facebook Login or Google Sign-In), 
                                you are also subject to the terms and privacy policies of those services.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>13. Termination</h2>
                            <p>
                                We reserve the right to suspend or terminate your account and access to the Service at 
                                our sole discretion, without notice, for conduct that we believe violates these Terms or 
                                is harmful to other users, us, or third parties, or for any other reason.
                            </p>
                            <p>
                                You may terminate your account at any time by contacting us. Upon termination, your right 
                                to use the Service will immediately cease.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>14. Changes to Terms</h2>
                            <p>
                                We reserve the right to modify these Terms at any time. If we make material changes, we 
                                will notify you by email or by posting a notice on our Service prior to the change becoming 
                                effective. Your continued use of the Service after the effective date of the revised Terms 
                                constitutes your acceptance of the changes.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>15. Governing Law & Jurisdiction</h2>
                            <p>
                                These Terms shall be governed by and construed in accordance with the laws of India, 
                                without regard to its conflict of law provisions. Any legal action or proceeding arising 
                                out of or related to these Terms shall be brought exclusively in the courts of 
                                <strong> Bangalore, Karnataka, India</strong>.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>16. Severability</h2>
                            <p>
                                If any provision of these Terms is held to be invalid, illegal, or unenforceable, the 
                                remaining provisions shall continue in full force and effect. The invalid provision shall 
                                be modified to the minimum extent necessary to make it valid and enforceable.
                            </p>
                        </div>

                        <div className={styles.terms_section}>
                            <h2>17. Contact Information</h2>
                            <p>
                                If you have any questions about these Terms of Service, please contact us at:
                            </p>
                            <div className={styles.contact_info}>
                                <p><strong>Email:</strong> info@thick-wallet.com</p>
                                <p><strong>Website:</strong> thick-wallet.com</p>
                            </div>
                        </div>

                        <div className={styles.terms_footer}>
                            <p>
                                By using Thick Wallet, you acknowledge that you have read, understood, and agree to 
                                be bound by these Terms of Service. For information about how we handle your data, 
                                please read our{' '}
                                <span 
                                    className={styles.link_text}
                                    onClick={handlePrivacyClick}
                                    onKeyDown={handlePrivacyKeyDown}
                                    tabIndex={0}
                                    role="link"
                                    aria-label="View Privacy Policy"
                                >
                                    Privacy Policy
                                </span>.
                            </p>
                        </div>
                    </section>
                </div>
            </main>
            <ScrollToTop />
            <FooterSection />
        </div>
    )
}

export default TermsOfService
